/**
 * Authentication Composable
 * Centralized authentication state management
 */
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import * as authService from '@/services/authService';
import { showSuccess, showError } from '@/utils/toast';

// Shared state across all components
const user = ref(null);
const accessToken = ref(null);
const refreshToken = ref(null);
const isLoading = ref(false);

// Initialize from localStorage
function initializeAuth() {
    const storedUser = localStorage.getItem('user');
    const storedAccessToken = localStorage.getItem('access_token');
    const storedRefreshToken = localStorage.getItem('refresh_token');

    if (storedUser && storedAccessToken && storedRefreshToken) {
        try {
            user.value = JSON.parse(storedUser);
            accessToken.value = storedAccessToken;
            refreshToken.value = storedRefreshToken;
        } catch (error) {
            console.error('Failed to parse stored user data:', error);
            clearAuth();
        }
    }
}

// Clear authentication state
function clearAuth() {
    user.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
}

// Save authentication state
function saveAuth(authData) {
    user.value = authData.user;
    accessToken.value = authData.access_token;
    refreshToken.value = authData.refresh_token;

    localStorage.setItem('user', JSON.stringify(authData.user));
    localStorage.setItem('access_token', authData.access_token);
    localStorage.setItem('refresh_token', authData.refresh_token);
}

export function useAuth() {
    const router = useRouter();

    // Initialize on first use
    if (!user.value && !accessToken.value) {
        initializeAuth();
    }

    // Computed properties
    const isAuthenticated = computed(() => !!accessToken.value);
    const currentUser = computed(() => user.value);

    /**
     * Login user
     */
    async function loginUser(username, password) {
        isLoading.value = true;
        try {
            const response = await authService.login(username, password);

            if (response.success && response.data) {
                saveAuth(response.data);
                showSuccess(response.message || 'Login successful');
                return { success: true };
            } else {
                throw new Error(response.message || 'Login failed');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Login failed. Please try again.';
            showError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            isLoading.value = false;
        }
    }

    /**
     * Register new user
     */
    async function registerUser(userData) {
        isLoading.value = true;
        try {
            const response = await authService.register(userData);

            if (response.success && response.data) {
                saveAuth(response.data);
                showSuccess(response.message || 'Registration successful');
                return { success: true };
            } else {
                throw new Error(response.message || 'Registration failed');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Registration failed. Please try again.';
            showError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            isLoading.value = false;
        }
    }

    /**
     * Logout user
     */
    async function logoutUser() {
        isLoading.value = true;
        try {
            if (refreshToken.value) {
                await authService.logout(refreshToken.value);
            }
            clearAuth();
            showSuccess('Logged out successfully');
            router.push({ name: 'login' });
        } catch (error) {
            // Even if logout API fails, clear local state
            clearAuth();
            router.push({ name: 'login' });
        } finally {
            isLoading.value = false;
        }
    }

    /**
     * Refresh access token
     */
    async function refreshAccessToken() {
        try {
            if (!refreshToken.value) {
                throw new Error('No refresh token available');
            }

            const response = await authService.refreshToken(refreshToken.value);

            if (response.success && response.data) {
                accessToken.value = response.data.access_token;
                localStorage.setItem('access_token', response.data.access_token);

                // Update user data if provided
                if (response.data.user) {
                    user.value = response.data.user;
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                }

                return response.data.access_token;
            } else {
                throw new Error('Token refresh failed');
            }
        } catch (error) {
            console.error('Token refresh failed:', error);
            clearAuth();
            router.push({ name: 'login' });
            throw error;
        }
    }

    /**
     * Get user profile
     */
    async function fetchProfile() {
        isLoading.value = true;
        try {
            const response = await authService.getProfile();

            if (response.success && response.data) {
                user.value = response.data;
                localStorage.setItem('user', JSON.stringify(response.data));
                return { success: true, data: response.data };
            } else {
                throw new Error('Failed to fetch profile');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.message ||
                'Failed to fetch profile';
            showError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            isLoading.value = false;
        }
    }

    /**
     * Update user profile
     */
    async function updateUserProfile(profileData) {
        isLoading.value = true;
        try {
            const response = await authService.updateProfile(profileData);

            if (response.success) {
                // Refresh profile to get updated data
                await fetchProfile();
                showSuccess(response.message || 'Profile updated successfully');
                return { success: true };
            } else {
                throw new Error(response.message || 'Failed to update profile');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.message ||
                'Failed to update profile';
            showError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            isLoading.value = false;
        }
    }

    /**
     * Change password
     */
    async function changeUserPassword(oldPassword, newPassword) {
        isLoading.value = true;
        try {
            const response = await authService.changePassword(oldPassword, newPassword);

            if (response.success) {
                showSuccess(response.message || 'Password changed successfully. Please login again.');
                // Clear auth and redirect to login
                clearAuth();
                router.push({ name: 'login' });
                return { success: true };
            } else {
                throw new Error(response.message || 'Failed to change password');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.message ||
                'Failed to change password';
            showError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            isLoading.value = false;
        }
    }

    /**
     * Upload user avatar
     */
    async function uploadAvatar(file) {
        isLoading.value = true;
        try {
            const response = await authService.uploadAvatar(file);

            if (response.success) {
                // Refresh profile to get updated data
                await fetchProfile();
                return { success: true, data: response.data };
            } else {
                throw new Error(response.message || 'Failed to upload avatar');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.message ||
                'Failed to upload avatar';
            showError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            isLoading.value = false;
        }
    }

    return {
        // State
        user: currentUser,
        isAuthenticated,
        isLoading,

        // Actions
        loginUser,
        registerUser,
        logoutUser,
        refreshAccessToken,
        fetchProfile,
        updateUserProfile,
        changeUserPassword,
        uploadAvatar,
        clearAuth
    };
}
